import { onBeforeUnmount, watch } from 'vue';

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(',');

// useFocusTrap(containerRef, openRef, { onEscape })
//
// While `openRef.value === true`:
//   - Tab/Shift+Tab cycle within the container (no escape to background page)
//   - Esc fires `onEscape` (typically the close handler)
//   - On open: focus first focusable inside container
//   - On close: return focus to whatever was active before opening
//
// Container can be a Vue ref (template ref) or a function returning the
// element (useful when the element is conditionally rendered).
export function useFocusTrap(containerRef, openRef, { onEscape } = {}) {
    // SSR-safe: every operation in this composable touches document /
    // requestAnimationFrame / HTMLElement which only exist in a browser.
    // The watch below is { immediate: true } so it fires DURING Vue SSR
    // render, which would crash Node with "document is not defined".
    // Bail out cleanly when there's no DOM.
    if (typeof document === 'undefined') {
        return;
    }

    let returnTarget = null;

    const resolveContainer = () => {
        const v = typeof containerRef === 'function' ? containerRef() : containerRef.value;
        // Some refs unwrap to the component instance; prefer .$el if present.
        return v?.$el ?? v ?? null;
    };

    const focusables = (root) => Array.from(root.querySelectorAll(FOCUSABLE))
        .filter(el => !el.hasAttribute('inert') && el.offsetParent !== null);

    const onKeydown = (e) => {
        const root = resolveContainer();
        if (!root) return;

        if (e.key === 'Escape') {
            e.stopPropagation();
            if (typeof onEscape === 'function') onEscape();
            return;
        }

        if (e.key !== 'Tab') return;
        const items = focusables(root);
        if (items.length === 0) {
            e.preventDefault();
            return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
            if (active === first || !root.contains(active)) {
                e.preventDefault();
                last.focus();
            }
        } else if (active === last) {
            e.preventDefault();
            first.focus();
        }
    };

    const open = () => {
        returnTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        document.addEventListener('keydown', onKeydown, true);
        // Defer to next paint so the element is rendered before we focus it.
        requestAnimationFrame(() => {
            const root = resolveContainer();
            if (!root) return;
            const items = focusables(root);
            (items[0] ?? root).focus({ preventScroll: true });
        });
    };

    const close = () => {
        document.removeEventListener('keydown', onKeydown, true);
        if (returnTarget && document.contains(returnTarget)) {
            returnTarget.focus({ preventScroll: true });
        }
        returnTarget = null;
    };

    watch(openRef, (isOpen) => {
        if (isOpen) open();
        else close();
    }, { immediate: true });

    onBeforeUnmount(() => {
        document.removeEventListener('keydown', onKeydown, true);
    });
}
